import type { ReactNode } from 'react';
import { PROGRAMS } from '@erectus/shared';
import { Icon } from '@/ui/icons.js';

/** The programs, plus "measure only" (null). Used by onboarding and the Plan screen. */
export function ProgramPicker({
  value,
  onChange,
  allowNone = true,
}: {
  value: string | null;
  onChange(programId: string | null): void;
  allowNone?: boolean;
}): ReactNode {
  return (
    <div className="space-y-2" role="radiogroup" aria-label="Program">
      {PROGRAMS.map((program) => {
        const on = value === program.id;
        return (
          <button
            key={program.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(program.id)}
            className={`w-full rounded-2xl border p-4 text-left transition ${
              on ? 'border-ink bg-white ring-2 ring-ink' : 'border-line bg-white'
            }`}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-semibold">{program.name}</span>
              <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs font-semibold text-ink-soft">
                {program.weeks} weeks
              </span>
            </span>
            <span className="mt-1 block text-sm text-ink-soft">{program.summary}</span>
            <span className="mt-1 block text-xs text-muted">{program.forWhom}</span>
            {on ? (
              <ul className="mt-3 space-y-1">
                {program.commitment.map((line) => (
                  <li key={line} className="flex gap-2 text-sm">
                    <Icon name="check" size={16} strokeWidth={2.4} className="mt-0.5 shrink-0 text-[color:var(--color-state-good)]" />
                    {line}
                  </li>
                ))}
              </ul>
            ) : null}
          </button>
        );
      })}
      {allowNone ? (
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          onClick={() => onChange(null)}
          className={`w-full rounded-2xl border p-4 text-left transition ${
            value === null ? 'border-ink bg-white ring-2 ring-ink' : 'border-line bg-white'
          }`}
        >
          <span className="font-semibold">Not now</span>
          <span className="mt-1 block text-sm text-ink-soft">
            Just measure for now. You can start a program any time from Plan.
          </span>
        </button>
      ) : null}
    </div>
  );
}
